// src/pages/ServicesPage.tsx
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { services } from '@/data/services';
import PageHeader from '@/components/PageHeader';
import { useLocation, useNavigate } from 'react-router-dom';
import CtaGradient from '@/components/CtaGradient';
import FaqSection from '@/components/FaqSection';
import { serviceFaqs } from '@/data/serviceFaqs';
import { Button } from '@/components/ui/button';
import { OptimizedImage } from '@/components/ui/OptimizedImage';

const ServicesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>(services[0].id);

  // Get the active service
  const activeService = services.find((service) => service.id === activeTab);

  // Get service image path based on service ID
  const getServiceImagePath = (serviceId: string) => {
    return `/services-${serviceId}.svg`;
  };

  // Get the related FAQs for the active service
  const serviceFaqGroup =
    serviceFaqs.find((group) => group.id === activeTab) ||
    serviceFaqs.find((group) => group.id === 'general'); // Fallback to general if no specific FAQs

  // Effect to handle URL query parameters on initial load and when they change
  useEffect(() => {
    // Get the tab from query parameters
    const queryParams = new URLSearchParams(location.search);
    const tabParam = queryParams.get('tab');

    if (tabParam) {
      // Check if the tab param corresponds to a valid service ID
      const validService = services.find((service) => service.id === tabParam);

      if (validService) {
        setActiveTab(tabParam);
      }
    }
  }, [location.search]); // Re-run when search params change

  // Handle tab click to update active tab and URL without scrolling
  const handleTabClick = (serviceId: string) => {
    // Update the active tab state
    setActiveTab(serviceId);

    // Update URL without scrolling - using search params instead of hash
    const newUrl = `/services?tab=${serviceId}`;
    navigate(newUrl, { replace: true });
  };

  return (
    <div className="relative">
      {/* Use the new PageHeader component */}
      <PageHeader title="We translate ideas into digital stories" />

      {/* Services Navigation Tabs */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center md:justify-start gap-2 md:gap-4 mb-16">
            {services.map((service) => (
              <motion.button
                key={service.id}
                id={service.id}
                className={`px-4 py-2 md:px-6 md:py-3 rounded-full text-lg font-medium transition-all
                ${
                  activeTab === service.id
                    ? 'bg-primary text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => handleTabClick(service.id)}
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.97 }}>
                <div className="flex items-center gap-2">
                  <span className="hidden md:inline-block">{service.icon}</span>
                  <span>{service.title}</span>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Active Service Details */}
          {activeService && (
            <div className="max-w-full mx-auto">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}>
                {/* Modern two-column layout for SVG and description */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center my-24 max-w-6xl">
                  {/* Left column: Animated SVG */}
                  <motion.div
                    className="flex justify-start items-center"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8 }}>
                    <motion.div
                      whileHover={{ scale: 1.05, rotate: 1 }}
                      whileTap={{ scale: 0.98 }}
                      className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl shadow-lg p-8 w-full max-w-md aspect-square flex items-center justify-center overflow-hidden group">
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-secondary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        style={{ borderRadius: 'inherit' }}
                      />
                      <OptimizedImage
                        src={getServiceImagePath(activeService.id)}
                        alt={`${activeService.title} service`}
                        className="w-3/4 h-3/4 object-contain z-10"
                        priority={true}
                        withAnimation={true}
                        animationProps={{
                          animate: {
                            y: [0, -8, 0],
                          },
                          transition: {
                            repeat: Infinity,
                            duration: 3,
                            ease: 'easeInOut',
                          },
                        }}
                      />
                    </motion.div>
                  </motion.div>

                  {/* Right column: Description */}
                  <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}>
                    <p className="text-2xl md:text-3xl text-gray-700 leading-relaxed">
                      {activeService.description}
                    </p>
                  </motion.div>
                </div>

                <div className="bg-white">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mb-16">
                    <h2 className="text-4xl md:text-5xl font-bold">
                      What we offer
                    </h2>
                    <motion.div
                      className="h-1 w-24 bg-secondary mt-6"
                      initial={{ width: 0 }}
                      animate={{ width: 96 }}
                      transition={{ duration: 0.7, delay: 0.3 }}
                    />
                  </motion.div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {activeService.items.map((item, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 * idx }}
                        className="flex items-start">
                        <div className="h-8 w-8 mr-4 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary text-white">
                          <svg
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        <span className="text-xl md:text-2xl font-medium">
                          {item}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-white mt-16 mb-36">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <CtaGradient />
          </div>
        </div>
      </section>

      {/* FAQs Section - Service-specific FAQs */}
      {serviceFaqGroup && (
        <section className="bg-slate-100/70 py-28">
          <div className="container mx-auto px-4">
            <motion.div
              key={`faq-${activeTab}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="max-w-full">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mb-16">
                <h2 className="text-4xl md:text-5xl font-bold">
                  Your Questions Answered
                </h2>
                <motion.div
                  className="h-1 w-24 bg-secondary mt-6"
                  initial={{ width: 0 }}
                  animate={{ width: 96 }}
                  transition={{ duration: 0.7, delay: 0.3 }}
                />
              </motion.div>

              <div className="max-w-full">
                <FaqSection
                  faqs={serviceFaqGroup.faqs}
                  bgColor="transparent"
                  hideHeader={true}
                />
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Additional CTA Section below FAQs - styled to match FAQ section */}
      <section className="bg-slate-100/70 pb-28">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center bg-white p-12 rounded-2xl shadow-sm">
            <motion.h3
              className="text-3xl font-bold mb-6"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}>
              Still have questions?
            </motion.h3>
            <motion.p
              className="text-lg text-gray-600 mb-8"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}>
              Our team is ready to help you find the answers you need and
              determine the best solutions for your business.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}>
              <Button
                onClick={() => navigate('/contact')}
                size="lg"
                className="px-8 py-6 text-lg font-medium">
                Contact Us
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
